var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _class, _class2, _descriptor;

function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/**
 * @copyright
 * Copyright(c) 2019 SICK AG
 */
import { Component, Prop, Watch } from '@sick-davinci/ce-decorators';
import * as QuickChart from '@meistercharts/meistercharts';
import { style } from './Histogram.style';
import { resolveCssColor, toCssColorValue } from '../_utils/ColorTools.js';
import { AbstractBarChartGrouped } from '../bar-chart/bar-chart-grouped/AbstractBarChartGrouped.js';
import { safeNumberArray } from '../_utils/SanitizeTools.js';
import { createBalloonTooltipStyle } from '../_utils/TooltipTools.js';
/**
 * A label for a bar of a histogram
 */

export
/**
 * A histogram.
 *
 * A histogram should be used when the distribution of values is the main concern.
 * If the exact value of a bar is important a bar-chart should be used instead.
 *
 * @example
 * ```html
 * <davinci-element-container heading="Histogramm" style="width: 400px; height: 300px">
 *   <davinci-histogram values="[[4,5,6]]" palette="primary" bar-labels='[{"index": 0, "label": "1"}, {"index": 2, "label": "3"}]'></davinci-histogram>
 * </davinci-element-container>
 * ```
 */
let Histogram = (_dec = Component({
  tag: 'davinci-histogram',
  style
}), _dec2 = Reflect.metadata("design:paramtypes", []), _dec3 = Prop(), _dec4 = Reflect.metadata("design:type", Object), _dec5 = Prop(), _dec6 = Reflect.metadata("design:type", Boolean), _dec7 = Watch('noTooltip'), _dec8 = Reflect.metadata("design:paramtypes", []), _dec9 = Prop(), _dec10 = Reflect.metadata("design:type", Object), _dec11 = Prop(), _dec12 = Reflect.metadata("design:type", Object), _dec(_class = _dec2(_class = (_class2 = class Histogram extends AbstractBarChartGrouped {
  constructor() {
    super([0]); // default is the first color of the selected palette for all bars

    _defineProperty(this, "_computedLabels", []);

    _defineProperty(this, "_resolvedLabels", []);

    _defineProperty(this, "_userDefinedLabels", null);

    _initializerDefineProperty(this, "noTooltip", _descriptor, this);
  }
  /**
   * Stores the computed labels
   */


  /**
   * @override
   */
  createApi() {
    return QuickChart.createHistogram();
  }
  /**
   * The labels to be used for the bars.
   * If not defined, the labels will be computed automatically starting from 1
   * with justifyCategoryAxis treated as if 'SpaceGreedilyPreferRoundIndices' was set.
   *
   * Provide an empty array to turn off labels altogether.
   */


  get barLabels() {
    return this._userDefinedLabels;
  }

  set barLabels(labels) {
    this._computedLabels = []; // reset

    this._resolvedLabels = []; // reset

    this._userDefinedLabels = labels;

    this._update();
  }
  /**
   * @override
   */


  _getCategoryLabels() {
    return this._userDefinedLabels ? this._resolveLabels() : this._computeLabels();
  }
  /**
   * Whether tooltips are disabled.
   *
   * @default false (tooltips are enabled by default)
   */


  noTooltipWatcher() {
    this._update();
  }
  /**
   * The values to be used for the bars of the different data series.
   * A two dimensional array; the first dimension denotes the data series, the second dimension the values for the bars of the corresponding data series.
   * e.g. 'value[0][2,3,4]' stores the values for the bars of the 1st data series.
   * Currently only one data series is supported.
   *
   * @default []
   */


  get values() {
    // funnel the spread values back into one data-series
    const funneledValues = [[]];

    this._getValues().forEach(values => funneledValues[0].push(...values));

    return funneledValues;
  }

  set values(values) {
    if (values.length > 1) {
      // eslint-disable-next-line
      console.warn(`currently only one data series is supported. ${values.length} data series were provided`);
    } // spread the values so one value is equivalent to one bar group


    const spreadValues = [];
    safeNumberArray(values[0]).forEach(value => spreadValues.push([value]));

    this._setValues(spreadValues);
  }
  /**
   * The colors to be used for the data series.
   * The first element denotes the color for the first data series, the second element denotes the color for the second data series and so on.
   *
   * Each entry of this array is either interpreted as an index referring to the selected palette, or a css-color-value designation (e.g. 'purple', '#ff00ff', 'rgba(255,255,128,0.5)').
   *
   * If there are more data series than colors the array will be traversed from the beginning.
   *
   * @default [0] - use the first color of the current color-palette
   */


  get colors() {
    return this._getSeriesColors();
  }

  set colors(value) {
    this._setSeriesColors(value);
  }
  /**
   * @override
   */


  _createStyle(hostFontFamily) {
    const style = super._createStyle(hostFontFamily);

    if (!this._userDefinedLabels) {
      style.categoryAxisStyle.justifyTickContent = 'SpaceGreedilyPreferRoundIndices';
    }

    style.barSize = null; // is calculated by QuickChart automatically

    style.barColors = this._computeBarColors();
    style.minGapBetweenGroups = 0;
    style.maxGapBetweenGroups = 2;
    style.showTooltip = !this.noTooltip;

    if (style.showTooltip) {
      style.tooltipStyle = createBalloonTooltipStyle(hostFontFamily, this.shorthandLabels, this.decimalPlaces);
      style.activeGroupBackgroundColor = resolveCssColor('var(--davinci-color-background-elements-overlay-2)');
    }

    return style;
  }
  /**
   * Resolves the user defined labels to an array of strings.
   */


  _resolveLabels() {
    var _this$_userDefinedLab;

    const barCount = this.values[0].length;

    if (this._resolvedLabels.length === barCount) {
      // no need to recalculate, use old ones
      return this._resolvedLabels;
    }

    this._resolvedLabels = Array(barCount).fill('');
    (_this$_userDefinedLab = this._userDefinedLabels) === null || _this$_userDefinedLab === void 0 ? void 0 : _this$_userDefinedLab.forEach(userDefinedLabel => {
      if (userDefinedLabel.index >= 0 && userDefinedLabel.index < barCount) {
        this._resolvedLabels[userDefinedLabel.index] = userDefinedLabel.label;
      }
    });
    return this._resolvedLabels;
  }

  _computeLabels() {
    const barCount = this.values[0].length;

    if (this._computedLabels.length === barCount) {
      // no need to recalculate, use old ones
      return this._computedLabels;
    } // Create a label for each bar, visibility is filtered later


    this._computedLabels = Array.from(Array(barCount), (_, index) => (index + 1).toString());
    return this._computedLabels;
  }
  /**
   * Computes the colors for the data series.
   *
   * The first dimension denotes the data series, the second dimension the color.
   *
   * @returns the colors to be used for the data series
   */


  _computeBarColors() {
    return [this._getSeriesColors().map(color => toCssColorValue(color, this.palette))];
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "barLabels", [_dec3, _dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "barLabels"), _class2.prototype), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "noTooltip", [_dec5, _dec6], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class2.prototype, "noTooltipWatcher", [_dec7, _dec8], Object.getOwnPropertyDescriptor(_class2.prototype, "noTooltipWatcher"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "values", [_dec9, _dec10], Object.getOwnPropertyDescriptor(_class2.prototype, "values"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "colors", [_dec11, _dec12], Object.getOwnPropertyDescriptor(_class2.prototype, "colors"), _class2.prototype)), _class2)) || _class) || _class);
